import { BenefitsCard, TemplateCard, Faq } from 'sections/Resources/FreightTemplates/types';

export const benefitsCardsData: BenefitsCard[] = [
  {
    id: 1,
    iconName: 'icon-badge-check.svg',
    heading: 'Standardized formats',
    description: 'Access our freight forwarding templates with standardized formats for consistency and ease.',
  },
  {
    id: 2,
    iconName: 'icon-pen.svg',
    heading: 'Editable & Printable',
    description: 'Customize and print templates to fit your needs, effortlessly.',
  },
  {
    id: 3,
    iconName: 'icon-globe.svg',
    heading: 'Global Compliance',
    description: 'Ensure global compliance with templates designed for international standards.',
  },
];

export const templatesCardData: TemplateCard[] = [
  {
    id: 1,
    iconName: 'icon-files.svg',
    heading: 'BOL (BIll of Lading)',
    url: '/resources/freight-templates/bill-of-lading',
    fileName: 'bill_of_lading',
  },
  {
    id: 2,
    iconName: 'icon-files.svg',
    heading: 'Commercial invoice',
    url: '/resources/freight-templates/commercial-invoice',
    fileName: 'commercial_invoice',
  },
  {
    id: 3,
    iconName: 'icon-files.svg',
    heading: 'Packing List',
    url: '/resources/freight-templates/packing-list',
    fileName: 'packing_list',
  },
  {
    id: 4,
    iconName: 'icon-files.svg',
    heading: 'Shipper’s Letter of Instruction',
    url: '/resources/freight-templates/sli',
    fileName: 'shipper\'s_letter_of_instruction',
  },
  {
    id: 5,
    iconName: 'icon-files.svg',
    heading: 'Dangerous Goods Declaration',
    url: '/resources/freight-templates/dangerous-goods-declaration',
    fileName: 'dangerous_goods_declaration',
  },
  {
    id: 6,
    iconName: 'icon-files.svg',
    heading: 'Freight Quotation Template',
    url: '/resources/freight-templates/freight-quotation',
    fileName: 'freight_quotation',
  },
  {
    id: 7,
    iconName: 'icon-files.svg',
    heading: 'Certificate of Origin',
    url: '/resources/freight-templates/certificate-of-origin',
    fileName: 'certificate_of_origin',
  },
  {
    id: 8,
    iconName: 'icon-files.svg',
    heading: 'Proforma Invoice',
    url: '/resources/freight-templates/proforma-invoice',
    fileName: 'proforma_invoice',
  },
];

export const faqData: Faq[] = [
  {
    id: "1",
    question: "What are shipping documents?",
    answer: (cn: Function) => (
      `
      <p class='${cn('faq__answer-paragraph')}'>
        Think of shipping documents as the passport and biography for your cargo. They are the   
        collection of records such as invoices, lists, certificates, that tell customs, carriers, and banks   
        exactly what you are moving, who owns it, and where it came from. Without these, your goods   
        don’t just get delayed; they get stuck. They are the legal trail that ensures your cargo can cross   
        borders and transfer ownership legally.
      </p>
    `
    ),
  },
  {
    id: "2",
    question: "How many types of shipping documents are there?",
    answer: (cn: Function) => (
      `
      <p class='${cn('faq__answer-paragraph')}'>
        There isn't a fixed "magic number" because requirements change based on the country and   
        commodity, but you can generally group them into three main buckets: 
        <ol class='${cn('faq__answer-ordered-list')}'>
          <li class='${cn('faq__answer-list-item')}'>
            Commercial Documents: These prove the sale and value (e.g., Commercial Invoice, Packing List).  
          </li>
          <li class='${cn('faq__answer-list-item')}'>
            Transport Documents: These cover the physical movement (e.g., Bill of Lading, Air Waybill).    
          </li>
          <li class='${cn('faq__answer-list-item')}'>
            Government/Compliance Documents: These are for authorities (e.g., Certificate of Origin, Dangerous Goods Declaration).     
          </li>
        </ol>
      </p>
      `
    ),
  },
  {
    id: "3",
    question: "What document is required for every shipment?",
    answer: (cn: Function) => (
      `
      <p class='${cn('faq__answer-paragraph')}'>
        If you had to pick the absolute essential, it’s the Commercial Invoice. You cannot clear customs   
        or calculate duties without it because it establishes the value of the goods. However, in   
        practice, you almost never ship with just one document. For nearly every shipment, documents   
        such as the Commercial Invoice, the Packing List, and a Transport Document (like a Bill of   
        Lading or Air Waybill) are required.   
      </p>      
      `
    ),
  },
  {
    id: "4",
    question: "What is a shipping checklist?",
    answer: (cn: Function) => (
      `
      <p class='${cn('faq__answer-paragraph')}'>
        A shipping checklist is essentially your safety net. It’s a standardised list used by logistics   
        teams to verify that every necessary piece of paperwork is present and correct before the cargo   
        leaves the dock. It prevents the nightmare scenario of goods arriving at a destination port only   
        to be held up because a Certificate of Origin or a specific permit was forgotten back at the   
        origin.
      </p>
      `
    ),
  },
  {
    id: "5",
    question: "What documents are required for export shipping?",
    answer: (cn: Function) => (
      `
      <p class='${cn('faq__answer-paragraph')}'>
        For a standard general cargo export, you will almost always need:
        <ul class='${cn('faq__answer-unordered-list')}'>
          <li class='${cn('faq__answer-list-item')}'>
            Commercial Invoice: The bill of sale.
          </li>
          <li class='${cn('faq__answer-list-item')}'>
            Packing List: Detailed breakdown of what is in every box.
          </li>
          <li class='${cn('faq__answer-list-item')}'>
            Bill of Lading (or Air Waybill): The contract with the carrier.
          </li>
          <li class='${cn('faq__answer-list-item')}'>
            Certificate of Origin: Proof of where the goods were made (often required for tax treaties).
          </li>
          <li class='${cn('faq__answer-list-item')}'>
            Export License: (If applicable to your specific goods).
          </li>
          <li class='${cn('faq__answer-list-item')}'>
            Insurance Certificate: Proof that the cargo is covered against loss or damage.
          </li>
        </ul>
      </p>
      `
    ),
  },
  {
    id: "6",
    question: "What is FCR document?",
    answer: (cn: Function) => (
      `
      <p class='${cn('faq__answer-paragraph')}'>
        FCR stands for Forwarder’s Cargo Receipt. It is exactly what it sounds like, a document issued   
        by your freight forwarder confirming that they have physically received your goods and they are   
        now in their custody. It acts as proof that the seller has handed over the cargo to the forwarder,   
        which is often a trigger for the buyer to release payment in certain trade agreements.
      </p>
      `
    ),
  },
  {
    id: "7",
    question: "Is FCR the same as Bill of lading?",
    answer: (cn: Function) => (
      `
      <p class='${cn('faq__answer-paragraph')}'>
        No, and confusing them can be risky.
        <ul class='${cn('faq__answer-unordered-list')}'>
          <li class='${cn('faq__answer-list-item')}'>
            A Bill of Lading (BL) is a contract of carriage and a document of title, meaning it proves who legally owns the cargo and controls its release.
          </li>
          <li class='${cn('faq__answer-list-item')}'>
            An FCR is just a receipt. It confirms the forwarder has the goods, but it is not a contract of carriage and usually doesn't convey ownership (title) of the goods in the same way a Negotiable Bill of Lading does.
          </li>
        </ul>
      </p>
      `
    ),
  },
  {
    id: "8",
    question: "What is an example of freight forwarding?",
    answer: (cn: Function) => (
      `
      <p class='${cn('faq__answer-paragraph')}'>
        Imagine you are a furniture maker in Vietnam selling 500 chairs to a retailer in Germany. You don't own a ship or a fleet of trucks in Europe.
        <ol class='${cn('faq__answer-ordered-list')}'>
          <li class='${cn('faq__answer-list-item')}'>
            You hire a freight forwarder.
          </li>
          <li class='${cn('faq__answer-list-item')}'>
            The forwarder arranges a truck to pick up the chairs from your factory in Vietnam.
          </li>
          <li class='${cn('faq__answer-list-item')}'>
            They book space on an ocean vessel and handle the export customs paperwork.
          </li>
          <li class='${cn('faq__answer-list-item')}'>
            Once the ship arrives in Hamburg, their partners handle the import clearance.
          </li>
          <li class='${cn('faq__answer-list-item')}'>
            Finally, they arrange a German truck to deliver the chairs to the retailer's warehouse. You deal with one contact (the forwarder), while they manage the complex chain of trucks, ships, and customs agents for you.
          </li>
        </ol>
      </p>
      `
    ),
  },
];